import {
  ArrowLeftIcon,
  CheckIcon,
  EnvelopeSimpleIcon,
  ProhibitIcon,
  TrashIcon,
  UserCheckIcon,
  XIcon,
} from '@phosphor-icons/react'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { useActivateClient } from '@/api/clients/activate-client'
import { useDeclineClient } from '@/api/clients/decline-client'
import { useDeleteClient } from '@/api/clients/delete-client'
import { useGetClientProfile } from '@/api/clients/get-client-profile'
import { useResendClientInvite } from '@/api/clients/resend-client-invite'
import { useUpdateClientActiveStatus } from '@/api/clients/update-client-active-status'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import {
  SideNav,
  SideNavContent,
  SideNavHeader,
  SideNavLayout,
  useSideNavTab,
} from '@/components/layout/side-nav'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { EmptyState } from '@/components/ui/empty-state'
import { Icon } from '@/components/ui/icon'
import { Link } from '@/components/ui/link'
import { SmartActionButton } from '@/components/ui/smart-action-button'
import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { API_ERRORS } from '@/constants'
import { popupFactory, routerFactory, toastFactory } from '@/factories'
import { getPathString } from '@/factories/create-router-factory'
import { getAssetLink } from '@/helpers/get-asset-link'
import { useDesignVersionToggle } from '@/hooks'
import { UserRoleEnum } from '@/models/features/user.models'
import { getClientStatusColor } from '@/pages/clients/clients-table/client-table.page.utils'
import { selectUserRole } from '@/reducers/userDataReducer'
import { getApiErrorMessage, getApiErrorStatus } from '@/utils/error'

import { CLIENT_PROFILE_TAB, getNavGroups, getNavItems } from './client-profile.constants'
import * as S from './client-profile.styled'
import { ClientProfileTab } from './client-profile.types'
import { getModuleAvailability, getUnitLabel, resolveActiveTab } from './client-profile.utils'
import { GeneralInformation } from './general-information'
import { GiftCards } from './gift-cards'
import { GuestPasses } from './guest-passes'
import { Inspections } from './inspections'
import { KeylessAccess } from './keyless-access'
import { Maintenance } from './maintenance'
import { Packages } from './packages'
import { RecentActivities } from './recent-activities'
import { Reservations } from './reservations'
import { Visitors } from './visitors'

export const ClientProfilePage = () => {
  const { t } = useTranslation(['clients', 'common'])
  const { clientId } = useParams<{ clientId: string }>()

  const { redirect, toggle: handleUIToggle } = useDesignVersionToggle({
    group: 'clients',
    screen: 'ClientProfile',
    to: { params: { clientId: clientId! }, path: '/dashboard/clients/:clientId/legacy' },
    version: 'new',
  })

  const { navigate } = routerFactory.useRouter()

  const userRole = useSelector(selectUserRole)
  const {
    data: clientData,
    error: clientError,
    isLoading,
  } = useGetClientProfile({ clientId: clientId! })

  const { mutateAsync: activateClient, isPending: isActivating } = useActivateClient(clientId!)
  const { mutateAsync: declineClient, isPending: isDeclining } = useDeclineClient(clientId!)
  const { mutateAsync: updateClientActiveStatus } = useUpdateClientActiveStatus(clientId!)
  const { mutateAsync: resendInvite, isPending: isResending } = useResendClientInvite()
  const { mutateAsync: deleteClient } = useDeleteClient()

  const isAdmin = userRole === UserRoleEnum.enum.Administrator
  const isTrainer = userRole === UserRoleEnum.enum.Trainer
  const isDeactivated = clientData?.active === false
  const isPending = !isDeactivated && !clientData?.user.isActive
  const isFrontDeskManager = userRole === UserRoleEnum.enum.ShiftManager
  const canToggleActive = userRole !== UserRoleEnum.enum.ShiftManager
  // Delete is restricted to Admin/Manager/PropertyManager - Front Desk (ShiftManager) must never see it.
  const canDelete =
    isAdmin ||
    userRole === UserRoleEnum.enum.Manager ||
    userRole === UserRoleEnum.enum.PropertyManager
  const isAnyLoading = isActivating || isDeclining || isResending

  // Note: gated on the resident's OWN building (like getModuleAvailability), not the globally
  // selected one - a profile opened from another building's table must still show Amenities.
  // Treated as available while loading so a ?tab=amenities deep link isn't rewritten too early.
  const amenityPassesAvailable = isLoading || !!clientData?.buildingId?.amenityPassesAvailable
  const moduleAvailability = getModuleAvailability(clientData, userRole, isAdmin, isLoading)

  const { activeTab, handleDirtyChange, handleTabChange } = useSideNavTab((rawTab) =>
    resolveActiveTab(rawTab, amenityPassesAvailable, isTrainer, moduleAvailability),
  )
  // Note: General information/Keyless access are plain content-sized cards that want the whole
  // page to scroll; the other tabs wrap a table that should stretch to fill this area and
  // scroll internally instead - see the note on `SideNavContent`'s `scroll` prop.
  const fillContent =
    activeTab !== CLIENT_PROFILE_TAB.GeneralInformation &&
    activeTab !== CLIENT_PROFILE_TAB.KeylessAccess

  const getTabHref = (tab: ClientProfileTab) =>
    getPathString({
      params: { clientId: clientId! },
      path: '/dashboard/clients/:clientId',
      queryParams: { tab },
    })

  const handleApprove = async () => {
    try {
      await activateClient()
      toastFactory.success(t('clients:profile.toasts.approveSuccess'))
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleDecline = async () => {
    try {
      await declineClient()
      toastFactory.success(t('clients:profile.toasts.declineSuccess'))
      navigate({ path: '/dashboard/clients' })
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleOpenApproveConfirm = () =>
    popupFactory.open(ConfirmationPopup, {
      children: (
        <Trans
          t={t}
          i18nKey="clients:profile.approveConfirmBody"
          values={{ building: clientData?.buildingId?.name ?? '', name: fullName }}
          components={[<strong key="resident-name" />]}
        />
      ),
      confirmIcon: CheckIcon,
      confirmText: t('clients:profile.approveConfirmText'),
      onConfirm: handleApprove,
      title: t('clients:profile.approveConfirmTitle'),
      variant: 'success',
    })

  const handleOpenDeclineConfirm = () =>
    popupFactory.open(ConfirmationPopup, {
      children: (
        <Trans
          t={t}
          i18nKey="clients:profile.rejectConfirmBody"
          values={{ name: fullName }}
          components={[<strong key="resident-name" />]}
        />
      ),
      confirmIcon: XIcon,
      confirmText: t('clients:profile.rejectConfirmText'),
      onConfirm: handleDecline,
      title: t('clients:profile.rejectConfirmTitle'),
      variant: 'danger',
    })

  const handleDeactivate = async () => {
    try {
      await updateClientActiveStatus({ active: false })
      toastFactory.success(t('clients:profile.toasts.deactivateSuccess'))
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleOpenDeactivateConfirm = () =>
    popupFactory.open(ConfirmationPopup, {
      children:
        'Deactivating this resident will revoke keyless access for any of their guests. Do you want to proceed?',
      confirmText: 'Yes, deactivate',
      onConfirm: handleDeactivate,
      title: 'Confirm deactivation',
      variant: 'danger',
    })

  const handleDelete = async () => {
    try {
      await deleteClient(clientData!.user.id)
      toastFactory.success(t('clients:profile.toasts.deleteSuccess'))
      navigate({ path: '/dashboard/clients' })
    } catch {
      // Note: unlike the other handlers on this page, this deliberately doesn't forward
      // getApiErrorMessage(error) - a delete failure can surface a raw backend/DB error (e.g. a
      // foreign key constraint message), which isn't safe or readable to show a user verbatim.
      toastFactory.error(t('clients:profile.toasts.deleteError'))
    }
  }

  const handleOpenDeleteConfirm = () =>
    popupFactory.open(ConfirmationPopup, {
      children: (
        <Trans
          t={t}
          i18nKey="clients:profile.actionsMenu.deleteConfirmBody"
          values={{
            name: `${clientData?.user.firstName ?? ''} ${clientData?.user.lastName ?? ''}`.trim(),
          }}
          components={[<strong key="resident-name" />]}
        />
      ),
      confirmIcon: TrashIcon,
      confirmText: t('clients:profile.actionsMenu.deleteConfirmText'),
      onConfirm: handleDelete,
      title: t('clients:profile.actionsMenu.deleteConfirmTitle'),
      variant: 'danger',
    })

  const handleActivate = async () => {
    try {
      await updateClientActiveStatus({ active: true })
      toastFactory.success(t('clients:profile.toasts.activateSuccess'))
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleResendInvite = async () => {
    try {
      await resendInvite(clientData!.user.email)
      toastFactory.success(t('clients:profile.toasts.resendInviteSuccess'))
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  if (redirect) return redirect

  const fullName = clientData ? `${clientData.user.firstName} ${clientData.user.lastName}` : ''
  const status = clientData?.statusPMS?.status ?? null
  // Note: deleted residents 404 (the BE soft-deletes them), and several tables still link to them.
  const isNotFound = !!clientError && getApiErrorStatus(clientError) === API_ERRORS.NOT_FOUND

  return (
    <Page centered fillHeight={fillContent}>
      <PageHeader
        title={t('clients:profile.title')}
        loading={isLoading}
        backButton={
          <Link navigateBack size="small" use="secondary" to={{ path: '/dashboard/clients' }}>
            <Icon icon={ArrowLeftIcon} size="1.4rem" />
            {t('common:actions.back')}
          </Link>
        }
        actions={
          <>
            <ToggleSwitch
              falsyLabel={t('clients:list.designToggle.old')}
              truthyLabel={t('clients:list.designToggle.new')}
              checked={true}
              onChange={handleUIToggle}
            />
            {!isTrainer && clientData && (
              <S.HeaderActions>
                {isPending && !isFrontDeskManager && (
                  <>
                    <Button
                      size="small"
                      use="secondary"
                      loading={isDeclining}
                      onClick={handleOpenDeclineConfirm}
                    >
                      {t('clients:profile.rejectRequest')}
                      <Icon icon={XIcon} color="red500" />
                    </Button>
                    <Button
                      size="small"
                      use="secondary"
                      loading={isActivating}
                      onClick={handleOpenApproveConfirm}
                    >
                      {t('clients:profile.approveRequest')}
                      <Icon icon={CheckIcon} color="green500" />
                    </Button>
                  </>
                )}
                <SmartActionButton
                  label={t('clients:profile.actionsMenu.label')}
                  size="small"
                  disabled={isAnyLoading}
                  items={[
                    {
                      icon: EnvelopeSimpleIcon,
                      label: t('clients:profile.actionsMenu.resendInvite'),
                      loading: isResending,
                      onClick: handleResendInvite,
                    },
                    ...(!isPending && canToggleActive
                      ? [
                          isDeactivated
                            ? {
                                icon: UserCheckIcon,
                                label: t('clients:profile.actionsMenu.activate'),
                                onClick: () => void handleActivate(),
                                variant: 'success' as const,
                              }
                            : {
                                icon: ProhibitIcon,
                                label: t('clients:profile.actionsMenu.deactivate'),
                                onClick: handleOpenDeactivateConfirm,
                              },
                        ]
                      : []),
                    ...(canDelete
                      ? [
                          {
                            icon: TrashIcon,
                            label: t('clients:profile.actionsMenu.delete'),
                            onClick: handleOpenDeleteConfirm,
                            variant: 'danger' as const,
                          },
                        ]
                      : []),
                  ]}
                />
              </S.HeaderActions>
            )}
          </>
        }
      />

      {clientError ? (
        <EmptyState
          title={
            isNotFound ? t('clients:profile.notFound.title') : t('clients:profile.loadError.title')
          }
          description={
            isNotFound ? t('clients:profile.notFound.description') : getApiErrorMessage(clientError)
          }
          actions={
            <Link navigateBack size="small" use="secondary" to={{ path: '/dashboard/clients' }}>
              <Icon icon={ArrowLeftIcon} size="1.4rem" />
              {t('common:actions.back')}
            </Link>
          }
        />
      ) : (
        <SideNavLayout>
          <SideNav
            activeTab={activeTab}
            ariaLabel={fullName}
            getHref={getTabHref}
            groups={getNavGroups(t)}
            header={
              <SideNavHeader
                title={fullName}
                avatarSrc={getAssetLink({
                  file: clientData?.user.avatar ?? undefined,
                  path: 'chitown-avatar/avatar',
                })}
                subtitle={getUnitLabel(
                  clientData?.apartment ?? null,
                  clientData?.buildingNumber ?? null,
                )}
                badges={
                  status && (
                    <Badge color={getClientStatusColor(status)} size="small" variant="light">
                      {status}
                    </Badge>
                  )
                }
              />
            }
            items={getNavItems(t, { amenityPassesAvailable, isTrainer, moduleAvailability })}
            onTabChange={handleTabChange}
          />

          <SideNavContent scroll={fillContent}>
            {activeTab === CLIENT_PROFILE_TAB.GeneralInformation && (
              <GeneralInformation clientId={clientId!} onDirtyChange={handleDirtyChange} />
            )}
            {activeTab === CLIENT_PROFILE_TAB.Activity && !isTrainer && (
              <RecentActivities clientId={clientId!} />
            )}
            {activeTab === CLIENT_PROFILE_TAB.KeylessAccess && !isTrainer && (
              <KeylessAccess clientId={clientId!} onDirtyChange={handleDirtyChange} />
            )}
            {activeTab === CLIENT_PROFILE_TAB.Maintenance && <Maintenance clientId={clientId!} />}
            {activeTab === CLIENT_PROFILE_TAB.Inspections && <Inspections clientId={clientId!} />}
            {activeTab === CLIENT_PROFILE_TAB.Packages && <Packages clientId={clientId!} />}
            {activeTab === CLIENT_PROFILE_TAB.Visitors && <Visitors clientId={clientId!} />}
            {activeTab === CLIENT_PROFILE_TAB.Reservations && <Reservations clientId={clientId!} />}
            {activeTab === CLIENT_PROFILE_TAB.Amenities && amenityPassesAvailable && (
              <GuestPasses clientId={clientId!} />
            )}
            {activeTab === CLIENT_PROFILE_TAB.GiftCards && <GiftCards clientId={clientId!} />}
          </SideNavContent>
        </SideNavLayout>
      )}
    </Page>
  )
}
